<script setup>
import { computed } from 'vue'
import { usePage, Link, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import BrandLockup from '@/Components/UI/BrandLockup.vue'

const page = usePage()
const { t } = useI18n()
const flash = computed(() => page.props.flash || {})
const portal = computed(() => page.props.portal || {})
const currentLocale = computed(() => page.props.locale || 'en')

function setLang(lang) {
  if (lang === currentLocale.value) return
  router.get(window.location.pathname, { lang }, { preserveScroll: true, preserveState: false })
}
</script>

<template>
  <div class="portal-shell">
    <header class="portal-header">
      <span class="portal-brand"><BrandLockup /><span class="portal-brand__sub">· {{ t('driver_portal.brand.sub') }}</span></span>
      <div class="portal-actions">
        <span class="portal-user">{{ portal.owner_name }}</span>
        <Link href="/" class="portal-nav-link">{{ t('driver_portal.nav.dashboard') }}</Link>
        <Link href="/earnings" class="portal-nav-link">{{ t('driver_portal.nav.earnings') }}</Link>
        <Link href="/payouts" class="portal-nav-link">{{ t('driver_portal.nav.payouts') }}</Link>
        <Link href="/profile" class="portal-nav-link">{{ t('driver_portal.nav.profile') }}</Link>
        <div class="portal-lang">
          <button :class="['portal-lang__btn', currentLocale === 'en' && 'is-active']" type="button" @click="setLang('en')">EN</button>
          <span class="portal-lang__sep">·</span>
          <button :class="['portal-lang__btn', currentLocale === 'ru' && 'is-active']" type="button" @click="setLang('ru')">RU</button>
        </div>
        <Link href="/logout" method="delete" as="button" class="portal-logout">{{ t('driver_portal.nav.sign_out') }}</Link>
      </div>
    </header>
    <div v-if="flash.alert" class="portal-flash portal-flash--alert" role="alert">{{ flash.alert }}</div>
    <div v-if="flash.notice" class="portal-flash portal-flash--notice">{{ flash.notice }}</div>
    <main class="portal-main"><slot /></main>
  </div>
</template>

<style scoped>
.portal-shell { min-height: 100vh; background: var(--bg); color: var(--ink); }
.portal-header {
  background: var(--surface); border-bottom: 1px solid var(--line);
  padding: 0.75rem 1rem;
  display: flex; align-items: center; gap: 1rem;
}
.portal-brand {
  display: inline-flex; align-items: center; gap: 0.4rem;
  font-family: var(--font-display, var(--display));
  font-weight: 700; color: var(--ink);
}
.portal-brand__sub { color: var(--muted); font-weight: 500; }
.portal-actions {
  margin-left: auto;
  display: flex; align-items: center; gap: 0.75rem;
}
.portal-user { color: var(--inkSoft); font-size: 0.9375rem; }
.portal-nav-link {
  color: var(--inkSoft); text-decoration: none; font-size: 0.875rem;
}
.portal-nav-link:hover { color: var(--ink); }
.portal-lang { display: flex; align-items: center; gap: 0.25rem; }
.portal-lang__btn {
  background: transparent; border: 0; color: var(--muted);
  font-size: 0.8125rem; font-weight: 600; cursor: pointer; padding: 0.1rem 0.2rem;
  letter-spacing: 0.03em;
}
.portal-lang__btn.is-active { color: var(--accent); }
.portal-lang__sep { color: var(--line); font-size: 0.75rem; }
.portal-logout {
  background: transparent; border: 1px solid var(--line); color: var(--ink);
  padding: 0.4rem 0.75rem; border-radius: var(--radius-button, 8px);
  font-size: 0.875rem; cursor: pointer;
}
.portal-flash {
  max-width: 48rem; margin: 1rem auto;
  padding: 0.75rem 1rem; border-radius: 0.5rem; font-size: 0.9375rem;
}
.portal-flash--alert {
  background: color-mix(in oklch, var(--danger) 12%, var(--surface));
  color: var(--danger);
}
.portal-flash--notice {
  background: color-mix(in oklch, var(--ok) 12%, var(--surface));
  color: var(--ok);
}
.portal-main { max-width: 48rem; margin: 0 auto; padding: 1.5rem 1rem 3rem; }
</style>
